import React, { useRef, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useGlobalStore } from '../../../common/XDataGrid/common';
import { errorToastify, infoToastify } from '../../../util/toast';
import { SERVER_URL, METHOD_POST } from '../../../store/constants/api';
import { headersAuth, headersAuthSendReceiveJson } from '../../../store/headers';
import { apiHandleResponse } from '../../../util/api';
import { http } from '../../../store/api/common';
import { ToastMessage } from '../../../common';

interface DocumentRow {
    file: File;
    name: string;
    updatedBy: string;
    uploadTime: string;
    documentType: number;
    type: string;
}

interface ContractEntry {
    contractName: string;
    documents: DocumentRow[];
}

interface Props {
    open: boolean;
    onClose: () => void;
    directoryId: number | string;
    handleRefresh: () => void;
}

const DOCUMENT_TYPE_OPTIONS = [
    { value: 1, label: 'Contract' }
];

const ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const ACCEPTED_MIME = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const MAX_CONTRACTS = 5;
const MAX_FILES_PER_CONTRACT = 5;
const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;


function buildDocumentRows(files: File[], username: string): DocumentRow[] {
    const uploadTime = new Date().toLocaleString();
    return files.map(file => ({
        file,
        name: file.name,
        updatedBy: username,
        uploadTime,
        documentType: 1,
        type: file.type,
    }));
}

function mergeFiles(existing: DocumentRow[], incoming: File[], username: string): DocumentRow[] {
    const existingNames = new Set(existing.map(r => r.name));
    const newRows = buildDocumentRows(
        incoming.filter(f => !existingNames.has(f.name)),
        username
    );
    return [...existing, ...newRows];
}

function getTotalBytes(rows: DocumentRow[]): number {
    return rows.reduce((sum, r) => sum + r.file.size, 0);
}

function formatMB(bytes: number): string {
    return (bytes / (1024 * 1024)).toFixed(1);
}

export function AutomaticContractUploadDialog({ open, onClose, directoryId, handleRefresh }: Props) {
    const currentUser = useGlobalStore((x: any) => x.session.user);
    const username = currentUser?.username || '';

    const [contracts, setContracts] = useState<ContractEntry[]>([]);
    const [contractName, setContractName] = useState('');
    const [documentRows, setDocumentRows] = useState<DocumentRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [contractNameError, setContractNameError] = useState('');
    const [filesError, setFilesError] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClose = () => {
        if (isLoading) return;
        resetForm();
        onClose();
    };

    const resetForm = () => {
        setContracts([]);
        setContractName('');
        setDocumentRows([]);
        setContractNameError('');
        setFilesError('');
        setIsDragOver(false);
    };

    const addFiles = (files: File[]) => {
        const valid = files.filter(f => ACCEPTED_MIME.includes(f.type));
        if (valid.length === 0) return;

        const merged = mergeFiles(documentRows, valid, username);

        if (merged.length > MAX_FILES_PER_CONTRACT) {
            setFilesError(
                `A contract can have at most ${MAX_FILES_PER_CONTRACT} files. ` +
                `You currently have ${documentRows.length} file${documentRows.length === 1 ? '' : 's'}.`
            );
            return;
        }

        const totalBytes = getTotalBytes(merged);

        if (totalBytes > MAX_FILE_SIZE_BYTES) {
            setFilesError(
                `Total file size exceeds ${MAX_FILE_SIZE_MB} MB ` +
                `(${formatMB(totalBytes)} MB). Please remove some files.`
            );
            return;
        }

        setFilesError('');

        if (documentRows.length === 0 && valid.length > 0 && !contractName.trim()) {
            const firstName = valid[0].name.replace(/\.[^/.]+$/, '');
            setContractName(firstName);
            setContractNameError('');
        }

        setDocumentRows(merged);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            addFiles(Array.from(e.target.files));
            e.target.value = '';
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        addFiles(Array.from(e.dataTransfer.files));
    };

    const handleDeleteRow = (index: number) => {
        const updated = documentRows.filter((_, i) => i !== index);
        if (getTotalBytes(updated) <= MAX_FILE_SIZE_BYTES) {
            setFilesError('');
        }
        setDocumentRows(updated);
    };

    const handleViewRow = (row: DocumentRow) => {
        const url = URL.createObjectURL(row.file);
        window.open(url, '_blank');
    };

    const handleDocTypeChange = (index: number, value: number) => {
        setDocumentRows(prev =>
            prev.map((row, i) => (i === index ? { ...row, documentType: value } : row))
        );
    };

    const validate = (): boolean => {
        let valid = true;
        if (!contractName.trim()) {
            setContractNameError('Contract name is required');
            valid = false;
        }
        if (documentRows.length === 0) {
            setFilesError('Please upload at least one document');
            valid = false;
        }
        return valid;
    };

    const handleAddNextDocument = () => {
        if (!validate()) return;
        setContracts(prev => [...prev, { contractName: contractName.trim(), documents: documentRows }]);
        setContractName('');
        setDocumentRows([]);
        setContractNameError('');
        setFilesError('');
    };

    const handleRemoveSavedContract = (index: number) => {
        setContracts(prev => prev.filter((_, i) => i !== index));
    };


    const handleAddContract = () => {
        let valid = true;
        if (!contractName.trim()) {
            setContractNameError('Contract name is required');
            valid = false;
        }
        if (documentRows.length === 0) {
            setFilesError('Please upload at least one document');
            valid = false;
        }
        if (!valid) return;

        setContracts(prev => [...prev, { contractName: contractName.trim(), documents: documentRows }]);
        setContractName('');
        setDocumentRows([]);
        setContractNameError('');
        setFilesError('');
    };

    const handleLoad = async () => {
        if (contracts.length === 0) return;

        setIsLoading(true);
        const numericDirectoryId = Number(directoryId) || null;
        try {
            // Matches the same multipart/form-data pattern as the existing document upload API.
            // Files are sent as binary; indexed keys follow Contracts[i].Documents[j].X convention.
            const formData = new FormData();
            contracts.forEach((entry, i) => {
                formData.append(`Contracts[${i}].ContractName`, entry.contractName);
                if (numericDirectoryId !== null) {
                    formData.append(`Contracts[${i}].ContractDirectoryId`, String(numericDirectoryId));
                }
                entry.documents.forEach((doc, j) => {
                    formData.append(`Contracts[${i}].Documents[${j}].DocumentName`, doc.name);
                    formData.append(
                        `Contracts[${i}].Documents[${j}].Metadata`,
                        JSON.stringify({ type: doc.type, file_extension: doc.name.substring(doc.name.lastIndexOf('.')) })
                    );
                    formData.append(`Contracts[${i}].Documents[${j}].Document`, doc.file, doc.name);
                    formData.append(`Contracts[${i}].Documents[${j}].DocumentType`, String(doc.documentType));
                    formData.append(`Contracts[${i}].Documents[${j}].Description`, '');
                });
            });

            // headersAuth() omits Content-Type so the browser sets multipart boundary automatically
            const response = await fetch(`${SERVER_URL}/api/v2/cms/ai/template`, {
                method: METHOD_POST,
                headers: headersAuth(),
                body: formData,
            }).then(apiHandleResponse);

            infoToastify('Contract creation request submitted successfully');

            // Response is a direct array: [{ contractId, documents: [{ documentId, s3Key }] }]
            const responseContracts: any[] = Array.isArray(response) ? response : [];

            if (responseContracts.length > 0) {
                // 1. Call extract-and-store with contract IDs and s3 keys
                const extractPayload = {
                    contracts: responseContracts.map((c: any) => ({
                        contract_id: c.contractId,
                        s3_keys: c.documents.map((d: any) => d.s3Key),
                    })),
                    model: 'gpt-4o',
                };

                await fetch(`${SERVER_URL}/ai-api/v1/contract-metadata/extract-and-store`, {
                    method: METHOD_POST,
                    headers: headersAuthSendReceiveJson(),
                    body: JSON.stringify(extractPayload),
                }).then(apiHandleResponse);

                // 2. Call ai-api/v3/contract-info/{contractId}/documents for each document.
                // Match by index since the response preserves submission order but omits documentName.
                for (let i = 0; i < responseContracts.length; i++) {
                    const { contractId, documents: resDocs = [] } = responseContracts[i];
                    const srcDocs = contracts[i]?.documents ?? [];

                    for (let j = 0; j < resDocs.length; j++) {
                        const resDoc = resDocs[j];
                        const srcDoc = srcDocs[j];
                        const extension = srcDoc
                            ? srcDoc.name.substring(srcDoc.name.lastIndexOf('.'))
                            : '.pdf';
                        const mimeType = srcDoc?.type ?? 'application/pdf';
                        const filename = srcDoc?.name ?? '';

                        await http.post(`/ai-api/v3/contract-info/${contractId}/documents`, {
                            s3_key: resDoc.s3Key,
                            filename,
                            contract_id: contractId,
                            document_id: resDoc.documentId,
                            metadata: { type: mimeType, file_extension: extension },
                            description: null,
                        });
                    }
                }
            }

            handleClose();
            handleRefresh();
        }
        catch (err) {
            const { error: errorMessage, errorDetails, innerException } = (err as any)?.payload || {};
            errorToastify(
                <ToastMessage
                    errorMessage={errorMessage || 'Failed to submit contracts. Please try again.'}
                    errorDetails={errorDetails}
                    innerException={innerException}
                />
            );
        } finally {
            setIsLoading(false);
        }
};

    const currentContractNumber = Math.min(contracts.length + 1, MAX_CONTRACTS);
    const canAddContract = contractName.trim() !== '' && contracts.length < MAX_CONTRACTS;
    const addButtonLabel = `Add Contract (${documentRows.length} ${documentRows.length === 1 ? 'file' : 'files'})`;
    const isFormDisabled = isLoading || contracts.length >= MAX_CONTRACTS;
    const isFileAddDisabled = isFormDisabled || documentRows.length >= MAX_FILES_PER_CONTRACT;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" disableEscapeKeyDown={isLoading}>
            <DialogTitle>
                Add Contract (Automatic)
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            Contract {currentContractNumber} of {MAX_CONTRACTS}
                        </Typography>
                    </Grid>

                    {/* Contract Name + Browse button inline */}
                    <Grid item xs={12}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={ACCEPTED_EXTENSIONS.join(',')}
                            style={{ display: 'none' }}
                            onChange={handleFileInputChange}
                            disabled={isFileAddDisabled}
                        />
                        <TextField
                            label="Contract Name"
                            required
                            fullWidth
                            value={contractName}
                            onChange={e => {
                                setContractName(e.target.value);
                                if (e.target.value.trim()) setContractNameError('');
                            }}
                            error={Boolean(contractNameError)}
                            helperText={contractNameError}
                            disabled={isFormDisabled}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Browse files">
                                            <IconButton
                                                size="small"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={isFileAddDisabled}
                                                edge="end"
                                            >
                                                <FolderOpenIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {/* Compact drag-and-drop zone */}
                    <Grid item xs={12}>
                        <div
                            onDrop={isFileAddDisabled ? undefined : handleDrop}
                            onDragOver={isFileAddDisabled ? undefined : e => { e.preventDefault(); setIsDragOver(true); }}
                            onDragLeave={isFileAddDisabled ? undefined : () => setIsDragOver(false)}
                            onClick={isFileAddDisabled ? undefined : () => fileInputRef.current?.click()}
                            style={{
                                border: `2px dashed ${isDragOver ? '#1976d2' : '#bdbdbd'}`,
                                borderRadius: 8,
                                padding: '16px 24px',
                                textAlign: 'center',
                                cursor: isFileAddDisabled ? 'not-allowed' : 'pointer',
                                background: isFileAddDisabled ? '#f5f5f5' : isDragOver ? '#e3f2fd' : '#fafafa',
                                transition: 'all 0.2s',
                                userSelect: 'none',
                                opacity: isFileAddDisabled ? 0.5 : 1,
                            }}
                        >
                            <Typography variant="body2" color="textSecondary">
                                Drag and drop files here, or click to browse &nbsp;
                                <span style={{ fontSize: 11, color: '#9e9e9e' }}>
                                    (PDF, DOC, DOCX)
                                </span>
                            </Typography>
                        </div>
                        {filesError && (
                            <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: 'block' }}
                            >
                                {filesError}
                            </Typography>
                        )}
                    </Grid>

                    {/* Document metadata grid */}
                    {documentRows.length > 0 && (
                        <Grid item xs={12}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Updated By</TableCell>
                                        <TableCell>Upload Time</TableCell>
                                        <TableCell>Document Type</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {documentRows.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{row.updatedBy}</TableCell>
                                            <TableCell>{row.uploadTime}</TableCell>
                                            <TableCell>
                                                <FormControl size="small" sx={{ minWidth: 110 }}>
                                                    <InputLabel>Type</InputLabel>
                                                    <Select
                                                        value={row.documentType}
                                                        label="Type"
                                                        onChange={e => handleDocTypeChange(index, e.target.value as number)}
                                                        disabled={isLoading}
                                                    >
                                                        {DOCUMENT_TYPE_OPTIONS.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell>{row.type}</TableCell>
                                            <TableCell>
                                                <Tooltip title="View">
                                                    <IconButton color="primary" size="small" onClick={() => handleViewRow(row)} disabled={isLoading}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton color="primary" size="small" onClick={() => handleDeleteRow(index)} disabled={isLoading}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                    )}

                    {/* Saved contracts summary */}
                    {contracts.length > 0 && (
                        <Grid item xs={12}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Contract Name</TableCell>
                                        <TableCell>Documents</TableCell>
                                        <TableCell>Remove</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contracts.map((c, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>{c.contractName}</TableCell>
                                            <TableCell>{c.documents.length}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Remove">
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handleRemoveSavedContract(i)}
                                                        disabled={isLoading}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleAddContract}
                    disabled={isLoading || !canAddContract}
                >
                    {contracts.length >= MAX_CONTRACTS
                        ? `Maximum of ${MAX_CONTRACTS} contracts reached`
                        : addButtonLabel}
                </Button>

                <div style={{ display: 'flex', gap: 8 }}>
                    <Button onClick={handleClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleLoad}
                        disabled={isLoading || contracts.length === 0}
                        startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isLoading ? 'Submitting...' : `Load (${contracts.length} ${contracts.length === 1 ? 'contract' : 'contracts'})`}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}

export default AutomaticContractUploadDialog;
